'use client';

import clsx from 'clsx';
import Image from 'next/image';
import { FC, useLayoutEffect, useRef } from 'react';

import { ProofCtaSectionDto } from '@/components/ServicesLobbyPage/ProofCtaSection/types';
import AnimatedSubmitButton from '@/components/shared/AnimatedSubmitButton';
import useDevice from '@/hooks/useDevice';
import { useLazyVideoSource } from '@/hooks/useLazyVideoSource';
import { useViewportVideosPlayback } from '@/hooks/useViewportVideosPlayback';
import { gsap } from '@/lib/gsap';

import css from './style.module.scss';

interface Props {
	data: ProofCtaSectionDto;
}

const ProofCtaSection: FC<Props> = ({ data }: Props) => {
	const device = useDevice();
	const wrapperRef = useRef<HTMLDivElement>(null);

	const shouldLoadVideo = useLazyVideoSource(wrapperRef);
	useViewportVideosPlayback([wrapperRef]);

	useLayoutEffect(() => {
		const wrapper = wrapperRef.current;
		const circle = wrapper?.querySelector(`.${css.curvedText}`);

		if (!wrapper || !circle) return;

		const ctx = gsap.context(() => {
			gsap.timeline({
				scrollTrigger: {
					trigger: wrapper,
					start: 'top bottom',
					end: 'bottom+=100% top',
					scrub: true,
					onUpdate: ({ progress }) => {
						const scrollProgress = progress * 360;
						gsap.to(circle, {
							rotate: scrollProgress,
							ease: 'none',
							duration: 0
						});
					}
				}
			});
		}, wrapper);

		return () => {
			ctx.revert();
		};
	}, []);

	return (
		<section ref={wrapperRef} className={clsx('wrapper', css.wrapper)}>
			<div className={css.content}>
				<div className={css.videoWrapper}>
					<video loop muted playsInline preload="none" className={css.video} controls={false}>
						{shouldLoadVideo ? (
							<>
								<source
									src="/assets/video/mov/megaphone-with-head.mov"
									type='video/quicktime; codecs="hvc1"'
								/>
								<source src="/assets/video/webm/megaphone-with-head.webm" type="video/webm" />
							</>
						) : null}
					</video>
				</div>

				<AnimatedSubmitButton
					startIconComponent={<span className={css.ctaDotIcon}>&#8226;</span>}
					wrapperClassName={css.ctaBtn}
					type="link"
					href="/case-studies"
				>
					{data.ctaButtonLabel}
				</AnimatedSubmitButton>
			</div>

			<div className={css.curvedWrapper}>
				<div className={css.curvedContainer}>
					<div className={css.circle} />
				</div>

				<div className={css.curvedContainer}>
					<Image
						preload
						width={device === 'desktop' ? 1775 : 424}
						height={device === 'desktop' ? 1775 : 424}
						alt=""
						className={css.curvedText}
						src={
							device === 'desktop'
								? '/assets/images/circle-text-white.webp'
								: '/assets/images/circle-text-mobile-white.webp'
						}
					/>
				</div>
			</div>
		</section>
	);
};

export default ProofCtaSection;
